import { createUniqueId } from '@jack-henry/frontend-utils/functions';
import { EnrichedTableItem, EnrichedTableItemKey } from './tm-table-internal.types';
import type { TmTable } from './tm-table.component';
import {
    TableEventPayload,
    TmTableConfig,
    TmTableConfigMulti,
    TmTableEvent,
    TmTableReduceEvent,
} from './tm-table.types';

export const DEFAULT_REDUCER = <T>(
    e: TmTableReduceEvent,
    incoming: T[] = [],
    current: T[] = []
) => [...current, ...incoming];

export function dispatchTableEvent<T extends object, Event extends TmTableEvent>(
    table: TmTable<T>,
    type: Event,
    payload: TableEventPayload<T>[Event]
) {
    const event = new CustomEvent(type, {
        detail: payload,
    });
    table.dispatchEvent(event);
}

export function isMultiConfig<T extends object>(
    config: TmTableConfig<T> | TmTableConfigMulti<T>
): config is TmTableConfigMulti<T> {
    return 'views' in config && Array.isArray(config.views);
}

const ENRICHED_ORIGINAL = Symbol('Original object ref the proxy enriches');

/**
 * Enrich a table item by wrapping it in a proxy with additional properties.
 */
export function enrichTableItem<T extends object>(item: T): EnrichedTableItem<T> {
    if (isEnriched(item)) {
        return item;
    }

    Object.values(EnrichedTableItemKey).forEach(key => {
        if (key in item) {
            throw new Error(
                `Property key '${key}' is defined on table item but has special meaning to <tm-table>. Choose another key name to ensure correct behavior.`
            );
        }
    });

    let isActive = false;
    const tableId = createUniqueId();
    const enriched = new Proxy(item, {
        get(target, prop) {
            // provide escape hatch to unwrap
            if (prop === ENRICHED_ORIGINAL) {
                return item;
            }

            // accessors for enriched properties
            if (prop === EnrichedTableItemKey.TableId) {
                return tableId;
            }

            if (prop === EnrichedTableItemKey.IsActive) {
                return isActive;
            }

            return target[prop as keyof T];
        },
        set(target, prop, value) {
            if (prop === EnrichedTableItemKey.IsActive) {
                isActive = value;
                return true;
            }

            target[prop as keyof T] = value;

            return true;
        },
    }) as EnrichedTableItem<T>;

    return enriched;
}

/**
 * @returns The original object given an `EnrichedTableItem`
 * or the passed item if not.
 */
function getOriginal<T extends object>(object: T | EnrichedTableItem<T>): T {
    return (Reflect.get(object, ENRICHED_ORIGINAL) as T | undefined) || object;
}

export function isEnriched<T extends object>(
    object: T | EnrichedTableItem<T>
): object is EnrichedTableItem<T> {
    // if the original is same as the passed object, we know it isn't enriched
    // since what is passed is not a proxy
    return getOriginal(object) !== object;
}
/**
 * Maps an enriched table item back to a vanilla object so
 * consumers are unaware of these implementation details.
 */
export function unwrapEnriched<T extends object>(enriched: EnrichedTableItem<T>): T {
    const unwrapped = getOriginal(enriched);

    if (!unwrapped) {
        throw new Error('Could not unwrap enriched object');
    }

    return unwrapped;
}
